import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import StatCard from '../biodiversity-status-center/components/StatCard';
import TurtleHatchlingTable from '../biodiversity-status-center/components/TurtleHatchlingTable';
import TurtleNestingTable from '../biodiversity-status-center/components/TurtleNestingTable';

const MarineTurtleConservation = () => {
  const statsData = [
  {
    icon: 'Waves',
    iconBg: 'bg-info/20',
    iconColor: 'text-info',
    value: '19,103',
    label: 'Total Hatchlings Released',
    subtitle: '2014-2024 conservation efforts',
    change: 162.5
  },
  {
    icon: 'MapPin',
    iconBg: 'bg-success/20',
    iconColor: 'text-success',
    value: '8',
    label: 'Protected Nesting Sites',
    subtitle: 'Active monitoring zones'
  },
  {
    icon: 'Activity',
    iconBg: 'bg-secondary/20',
    iconColor: 'text-secondary',
    value: '337',
    label: 'Nesting Incidents Recorded',
    subtitle: 'Over 11-year monitoring period'
  },
  {
    icon: 'Users',
    iconBg: 'bg-primary/20',
    iconColor: 'text-primary',
    value: '450+',
    label: 'Community Volunteers',
    subtitle: 'Supporting conservation efforts'
  }];


  const turtleSpecies = [
  {
    name: 'Green Sea Turtle',
    scientificName: 'Chelonia mydas',
    image: "https://images.unsplash.com/photo-1655365225159-e6de86b2fefa",
    imageAlt: 'Green sea turtle swimming gracefully through crystal clear turquoise ocean waters with sunlight filtering from above',
    status: 'Endangered',
    description: 'Named for the greenish color of their cartilage and fat, Green Sea Turtles are the largest hard-shelled sea turtles. Adults can weigh up to 190 kg and feed primarily on seagrass and algae. They play a crucial role in maintaining healthy seagrass beds and coral reef ecosystems.'
  },
  {
    name: 'Hawksbill Turtle',
    scientificName: 'Eretmochelys imbricata',
    image: "https://images.unsplash.com/photo-1511168836158-bb7074a169eb",
    imageAlt: 'Hawksbill sea turtle with distinctive patterned shell swimming near vibrant coral reef in tropical waters',
    status: 'Critically Endangered',
    description: 'Recognizable by their narrow, pointed beak and beautiful patterned shells, Hawksbill Turtles are critically endangered. They feed primarily on sponges and play a vital role in reef health. Their shells have historically been used for tortoiseshell products, leading to severe population declines.'
  },
  {
    name: 'Olive Ridley Turtle',
    scientificName: 'Lepidochelys olivacea',
    image: "https://images.unsplash.com/photo-1617818866012-242d1f3b7359",
    imageAlt: 'Olive Ridley sea turtle with olive-colored heart-shaped shell resting on sandy beach during nesting season',
    status: 'Vulnerable',
    description: 'The smallest of the sea turtles, Olive Ridleys are known for their synchronized mass nesting events called "arribadas." They have an olive-colored heart-shaped shell and are omnivorous feeders. Despite being the most abundant sea turtle, they face threats from fishing bycatch and habitat loss.'
  }];


  const conservationPrograms = [
  {
    icon: 'Shield',
    title: 'Beach Patrol & Nest Protection',
    description: 'Round-the-clock monitoring of nesting beaches during peak season. Rangers and volunteers protect nests from predators, poachers, and environmental threats. Relocated nests when necessary to ensure maximum hatchling survival rates.',
    impact: '85% nest protection success rate'
  },
  {
    icon: 'Heart',
    title: 'Hatchling Release Program',
    description: 'Carefully timed releases of hatchlings to maximize survival chances. Community involvement in release events raises awareness and builds local stewardship. Monitoring programs track hatchling success and beach conditions.',
    impact: '19,103 hatchlings released since 2014'
  },
  {
    icon: 'Users',
    title: 'Community Education & Engagement',
    description: 'Educational programs for local communities, schools, and tourists about marine turtle conservation. Training programs for community volunteers and eco-tourism guides. Livelihood alternatives for former turtle egg collectors.',
    impact: '450+ active community volunteers'
  },
  {
    icon: 'Search',
    title: 'Research & Monitoring',
    description: 'Scientific research on nesting patterns, population dynamics, and migration routes. Tagging programs to track individual turtles and understand their life cycles. Data collection to inform conservation strategies and policy decisions.',
    impact: '11 years of comprehensive data'
  }];


  const threats = [
  {
    icon: 'AlertTriangle',
    title: 'Plastic Pollution',
    description: 'Marine debris and plastic waste pose ingestion and entanglement risks'
  },
  {
    icon: 'Anchor',
    title: 'Fishing Bycatch',
    description: 'Accidental capture in fishing nets and gear causes injury and mortality'
  },
  {
    icon: 'Home',
    title: 'Habitat Loss',
    description: 'Coastal development and beach erosion reduce available nesting sites'
  },
  {
    icon: 'Sun',
    title: 'Climate Change',
    description: 'Rising temperatures affect sand temperature and sex ratios of hatchlings'
  }];


  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative h-[400px] overflow-hidden">
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_12903615e-1765151405540.png"
            alt="Marine turtle swimming in crystal clear ocean waters with coral reef visible below, showcasing the pristine marine ecosystem of Puerto Princesa Subterranean River National Park"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
                Marine Turtle Conservation
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto drop-shadow-md">
                Protecting endangered sea turtles through community-driven conservation programs
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="container mx-auto px-4 -mt-16 relative z-10 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData?.map((stat, index) =>
            <StatCard key={index} stat={stat} />
            )}
          </div>
        </div>

        {/* Background Information Section */}
        <div className="container mx-auto px-4 mb-12">
          <div className="bg-card rounded-lg shadow-conservation p-8">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="BookOpen" size={32} className="text-primary" />
              <h2 className="text-3xl font-bold text-foreground">Background & Significance</h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-4">
                The waters surrounding Puerto Princesa Subterranean River National Park (PPSRNP) serve as critical habitat for several species of endangered marine turtles. These ancient mariners have navigated Earth's oceans for over 100 million years, playing vital roles in marine ecosystem health by maintaining seagrass beds, controlling jellyfish populations, and facilitating nutrient cycling between ocean and land.
              </p>
              
              <p className="text-muted-foreground leading-relaxed mb-4">
                Since 2014, PPSRNP has implemented comprehensive marine turtle conservation programs in response to declining populations caused by habitat loss, fishing bycatch, plastic pollution, and climate change. The park's protected beaches provide essential nesting grounds for Green Sea Turtles, Hawksbill Turtles, and Olive Ridley Turtles—all listed as threatened or endangered under international conservation frameworks.
              </p>
              
              <p className="text-muted-foreground leading-relaxed mb-6">
                Through collaborative efforts involving park rangers, marine biologists, local communities, and international conservation organizations, the program has successfully released over 19,000 hatchlings into the wild. Community engagement remains central to conservation success, with former turtle egg collectors now serving as dedicated protectors and eco-tourism guides, demonstrating how conservation can provide sustainable livelihoods while protecting biodiversity.
              </p>

              <div className="bg-primary/10 border-l-4 border-primary rounded-r-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="Lightbulb" size={24} className="text-primary mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Why Marine Turtles Matter</h3>
                    <p className="text-sm text-muted-foreground">
                      Marine turtles are indicator species—their health reflects the overall condition of marine ecosystems. They maintain seagrass beds that serve as nurseries for fish, control sponge populations on coral reefs, and transport nutrients from ocean to beach ecosystems. Protecting turtles means protecting entire marine food webs and the communities that depend on healthy oceans.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Species Section */}
        <div className="container mx-auto px-4 mb-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">Marine Turtle Species</h2>
            <p className="text-muted-foreground">Three endangered species nest in PPSRNP protected waters</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {turtleSpecies?.map((species, index) =>
            <div key={index} className="bg-card rounded-lg overflow-hidden shadow-conservation hover:shadow-conservation-lg animation-organic group">
                <div className="relative h-64 overflow-hidden">
                  <Image
                  src={species?.image}
                  alt={species?.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 animation-organic" />

                  <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-semibold ${
                species?.status === 'Critically Endangered' ? 'bg-error text-error-foreground' :
                species?.status === 'Endangered' ? 'bg-warning text-warning-foreground' :
                'bg-accent text-accent-foreground'}`
                }>
                    {species?.status}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-1">{species?.name}</h3>
                  <p className="text-sm text-muted-foreground italic mb-4">{species?.scientificName}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{species?.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Conservation Programs Section */}
        <div className="bg-muted/30 py-12 mb-12">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">Conservation Programs</h2>
              <p className="text-muted-foreground">Comprehensive initiatives protecting marine turtles and their habitats</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {conservationPrograms?.map((program, index) =>
              <div key={index} className="bg-card rounded-lg p-6 shadow-conservation">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-full p-3 flex-shrink-0">
                      <Icon name={program?.icon} size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">{program?.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3">{program?.description}</p>
                      <div className="bg-success/10 border border-success/20 rounded-lg px-3 py-2 inline-block">
                        <p className="text-xs font-medium text-success">{program?.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Data Tables Section */}
        <div className="container mx-auto px-4 mb-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">Conservation Data & Monitoring</h2>
            <p className="text-muted-foreground">Comprehensive tracking of hatchling releases and nesting activities (2014-2024)</p>
          </div>
          
          <div className="space-y-8">
            <TurtleHatchlingTable />
            <TurtleNestingTable />
          </div>
        </div>

        {/* Threats Section */}
        <div className="container mx-auto px-4 mb-12">
          <div className="bg-card rounded-lg shadow-conservation p-8">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="AlertCircle" size={32} className="text-error" />
              <h2 className="text-3xl font-bold text-foreground">Major Threats</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {threats?.map((threat, index) =>
              <div key={index} className="bg-muted/50 rounded-lg p-5 border border-border">
                  <div className="bg-error/10 rounded-full p-3 w-fit mb-4">
                    <Icon name={threat?.icon} size={24} className="text-error" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{threat?.title}</h3>
                  <p className="text-sm text-muted-foreground">{threat?.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="bg-primary/10 py-12 mb-12">
          <div className="container mx-auto px-4 text-center">
            <Icon name="Heart" size={48} className="text-primary mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-foreground mb-4">Support Marine Turtle Conservation</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
              Join our community of volunteers, participate in hatchling release events, or support conservation efforts through eco-tourism. Every action helps protect these ancient mariners for future generations.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="default" size="lg" iconName="Users">
                Volunteer Program
              </Button>
              <Button variant="outline" size="lg" iconName="Calendar">
                Upcoming Events
              </Button>
              <Button variant="outline" size="lg" iconName="Info">
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-8">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <Link to="/biodiversity-status-center" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Biodiversity Status Center
                  </Link>
                  <Link to="/research-data-portal" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Research Data Portal
                  </Link>
                  <Link to="/community-engagement-platform" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Community Engagement
                  </Link>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Conservation</h3>
                <div className="space-y-2">
                  <Link to="/visitor-planning-hub" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Visit the Park
                  </Link>
                  <Link to="/site-showcase-gallery" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Site Showcase
                  </Link>
                  <Link to="/interactive-conservation-map" className="block text-sm text-muted-foreground hover:text-primary animation-organic">
                    Interactive Map
                  </Link>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Connect</h3>
                <div className="flex gap-4">
                  <Icon name="Facebook" size={20} className="text-muted-foreground hover:text-primary cursor-pointer animation-organic" />
                  <Icon name="Twitter" size={20} className="text-muted-foreground hover:text-primary cursor-pointer animation-organic" />
                  <Icon name="Instagram" size={20} className="text-muted-foreground hover:text-primary cursor-pointer animation-organic" />
                  <Icon name="Mail" size={20} className="text-muted-foreground hover:text-primary cursor-pointer animation-organic" />
                </div>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                © 2025 Puerto Princesa Subterranean River National Park. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>);

};

export default MarineTurtleConservation;